import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Copy, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button, Input } from '../../components/common';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-xl bg-accent-primary flex items-center justify-center">
              <Copy className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-text-primary tracking-tight">MIMIQ</span>
          </Link>

          <h1 className="text-2xl font-bold text-text-primary mb-2">Welcome back</h1>
          <p className="text-text-secondary mb-8">Sign in to your account to continue</p>

          {error && (
            <div className="mb-6 p-4 bg-loss/10 border border-loss/20 rounded-lg text-loss text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label="Email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              leftIcon={<Mail className="w-4 h-4" />}
              required
            />

            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                leftIcon={<Lock className="w-4 h-4" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-text-muted hover:text-text-secondary"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                }
                required
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-border bg-bg-tertiary text-accent-primary focus:ring-accent-primary/20"
                />
                <span className="text-sm text-text-secondary">Remember me</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm text-accent-primary hover:text-accent-hover"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              loading={loading}
              className="w-full"
              size="lg"
            >
              Sign In
            </Button>
          </form>

          <p className="mt-8 text-center text-text-secondary">
            Don't have an account?{' '}
            <Link to="/register" className="text-accent-primary hover:text-accent-hover font-medium">
              Create account
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Branding */}
      <div className="hidden lg:flex flex-1 bg-bg-secondary border-l border-border items-center justify-center p-8">
        <div className="max-w-md text-center">
          <h2 className="text-3xl font-bold text-text-primary mb-4">
            Indian Stock Copy Trading
          </h2>
          <p className="text-text-secondary mb-8">
            Mirror the trades of expert traders automatically. Real-time execution with Firstock integration.
          </p>
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-bg-tertiary rounded-xl p-4">
              <div className="text-2xl font-bold text-profit">NSE</div>
              <div className="text-sm text-text-muted">National Stock Exchange</div>
            </div>
            <div className="bg-bg-tertiary rounded-xl p-4">
              <div className="text-2xl font-bold text-text-primary">NFO</div>
              <div className="text-sm text-text-muted">F&O Segment</div>
            </div>
            <div className="bg-bg-tertiary rounded-xl p-4">
              <div className="text-2xl font-bold text-text-primary">MCX</div>
              <div className="text-sm text-text-muted">Commodities</div>
            </div>
            <div className="bg-bg-tertiary rounded-xl p-4">
              <div className="text-2xl font-bold text-text-primary">BSE</div>
              <div className="text-sm text-text-muted">Bombay Stock Exchange</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
